import { z } from "zod"
import { type AuthPayloadType, AuthPayloadSchema } from "@antipartido-inc/contracts-ifaces"

const AuthPayloadValidation = z.object({
  userId: z.string({ required_error: "userId is required" }),
  token: z.string({ required_error: "token is required" }),
  refreshToken: z.string({ required_error: "refreshToken is required" }),
  tokenExpiry: z.number({ required_error: "tokenExpiry is required" }),
})

export class AuthPayload {
  constructor(
    private readonly _userId: string,
    private readonly _token: string,
    private readonly _refreshToken: string,
    private readonly _tokenExpiry: number,
  ) {}

  static create({ userId, token, refreshToken, tokenExpiry }: z.infer<typeof AuthPayloadValidation>) {
    const { success, error } = AuthPayloadValidation.safeParse({ userId, token, refreshToken, tokenExpiry })
    if (!success) throw new Error(error.message)
    return new AuthPayload(userId, token, refreshToken, tokenExpiry)
  }

  static createFromAPI({ userId, token, refreshToken, tokenExpiry }: AuthPayloadType) {
    const { success, error } = AuthPayloadSchema.safeParse({ userId, token, refreshToken, tokenExpiry })
    if (!success) throw new Error(error.message)

    return AuthPayload.create({ userId, token, refreshToken, tokenExpiry })
  }

  get userId() { return this._userId } // prettier-ignore
  get token() { return this._token } // prettier-ignore
  get refreshToken() { return this._refreshToken } // prettier-ignore
  get tokenExpiry() { return this._tokenExpiry } // prettier-ignore
  private get refreshTokenExpiryInMs() { return this.tokenExpiry * 1000 } // prettier-ignore

  private decodeJwtPayload(): { exp?: number } | null {
    try {
      const parts = this._token.split(".")
      if (parts.length !== 3) return null
      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
      return JSON.parse(decoded)
    } catch {
      return null
    }
  }

  isRefreshTokenExpired() {
    return Date.now() >= this.refreshTokenExpiryInMs
  }

  shouldRefresh(): boolean {
    const payload = this.decodeJwtPayload()
    if (!payload?.exp) return true
    const jwtExpiryInMs = payload.exp * 1000
    return Date.now() >= jwtExpiryInMs
  }

  serialize() {
    return {
      userId: this.userId,
      token: this.token,
      refreshToken: this.refreshToken,
      tokenExpiry: this.tokenExpiry,
    }
  }
}

export type AuthPayloadSerialized = ReturnType<AuthPayload["serialize"]>
